/** @jsxImportSource @emotion/react */
import { css } from '@emotion/react';
import { Button, TextToggle, TextToggleGroup, getColor, vars } from '@flodesk/grain';
import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import wandPatternImg from '@/components/app/product-creation-form/wand-pattern-pack.jpg';

// ─── Types ────────────────────────────────────────────────────────────────────

type PreviewData = {
  productName?: string;
  productDesc?: string;
  productImage?: string;
  price?: string;
  salePrice?: string;
  onSaleActive?: boolean;
  isRecurring?: boolean;
  recurringPrice?: string;
  bumpProduct?: string;
  bumpOfferTitle?: string;
  testimonialName?: string;
  testimonialText?: string;
  testimonialImage?: string;
  confirmTitle?: string;
  confirmDesc?: string;
  confirmBtnText?: string;
};

type ActiveTab = 'checkout' | 'confirmation';

// ─── Helpers ──────────────────────────────────────────────────────────────────

function formatPrice(p: string): string {
  return p.startsWith('$') ? p : `$${p}`;
}

function getEffectivePrice(data: PreviewData): string {
  const raw = data.isRecurring ? data.recurringPrice : data.price;
  if (!raw) return '';
  const suffix = data.isRecurring ? '/mo' : '';
  return formatPrice(raw) + suffix;
}

function getSalePrice(data: PreviewData): string {
  if (!data.salePrice || data.isRecurring) return '';
  return formatPrice(data.salePrice);
}

// ─── Checkout view ────────────────────────────────────────────────────────────

function CheckoutView({ data }: { data: PreviewData }) {
  const priceStr = getEffectivePrice(data);
  const salePriceStr = getSalePrice(data);
  const hasSale = !!salePriceStr && !!data.onSaleActive;
  const payAmount = hasSale ? salePriceStr : priceStr;

  return (
    <div
      css={css`
        display: flex;
        max-width: 900px;
        margin: 0 auto;
        padding: 48px 32px;
        gap: 48px;
      `}
    >
      {/* Left column */}
      <div
        css={css`
          flex: 1;
        `}
      >
        <h3
          css={css`
            font-size: 16px;
            font-weight: 500;
            margin-bottom: 16px;
            color: ${getColor(vars.colors.content)};
          `}
        >
          Contact information
        </h3>

        <div
          css={css`
            display: flex;
            gap: 12px;
            margin-bottom: 12px;
          `}
        >
          <input
            type="text"
            placeholder="First name"
            readOnly
            css={css`
              flex: 1;
              height: 44px;
              padding: 0 16px;
              border-radius: 6px;
              border: 1px solid ${getColor(vars.colors.border)};
              font-size: 14px;
              color: ${getColor(vars.colors.content)};
              outline: none;
              font-family: inherit;
            `}
          />
          <input
            type="text"
            placeholder="Last name"
            readOnly
            css={css`
              flex: 1;
              height: 44px;
              padding: 0 16px;
              border-radius: 6px;
              border: 1px solid ${getColor(vars.colors.border)};
              font-size: 14px;
              color: ${getColor(vars.colors.content)};
              outline: none;
              font-family: inherit;
            `}
          />
        </div>

        <input
          type="email"
          placeholder="Email"
          readOnly
          css={css`
            width: 100%;
            height: 44px;
            padding: 0 16px;
            border-radius: 6px;
            border: 1px solid ${getColor(vars.colors.border)};
            font-size: 14px;
            color: ${getColor(vars.colors.content)};
            outline: none;
            margin-bottom: 16px;
            font-family: inherit;
          `}
        />

        <label
          css={css`
            display: flex;
            align-items: center;
            gap: 8px;
            font-size: 14px;
            margin-bottom: 32px;
            cursor: pointer;
            color: ${getColor(vars.colors.content)};
          `}
        >
          <input
            type="checkbox"
            readOnly
            css={css`
              width: 16px;
              height: 16px;
              border-radius: 4px;
              border: 1px solid ${getColor(vars.colors.border)};
            `}
          />
          Sign up for my weekly newsletter
        </label>

        <h3
          css={css`
            font-size: 16px;
            font-weight: 500;
            margin-bottom: 16px;
            color: ${getColor(vars.colors.content)};
          `}
        >
          Payment details
        </h3>

        {/* Card option */}
        <div
          css={css`
            border-radius: 8px;
            border: 1px solid ${getColor(vars.colors.border)};
            overflow: hidden;
            margin-bottom: 16px;
          `}
        >
          <div
            css={css`
              display: flex;
              align-items: center;
              gap: 12px;
              padding: 0 16px;
              height: 44px;
              border-bottom: 1px solid ${getColor(vars.colors.border)};
            `}
          >
            <div
              css={css`
                width: 16px;
                height: 16px;
                border-radius: 50%;
                border: 5px solid ${getColor(vars.colors.content)};
              `}
            />
            <svg width="20" height="14" viewBox="0 0 20 14" fill="none">
              <rect x="0.5" y="0.5" width="19" height="13" rx="2" stroke="#ccc" />
              <rect x="0" y="3" width="20" height="3" fill="#ccc" />
            </svg>
            <span
              css={css`
                font-size: 14px;
                color: ${getColor(vars.colors.content)};
              `}
            >
              Card
            </span>
          </div>
          <div
            css={css`
              padding: 12px 16px;
            `}
          >
            <input
              type="text"
              placeholder="1234 1234 1234 1234"
              readOnly
              css={css`
                width: 100%;
                height: 40px;
                padding: 0 12px;
                border-radius: 6px;
                border: 1px solid ${getColor(vars.colors.border)};
                font-size: 14px;
                outline: none;
                margin-bottom: 12px;
                font-family: inherit;
              `}
            />
            <div
              css={css`
                display: flex;
                gap: 12px;
                margin-bottom: 12px;
              `}
            >
              <input
                type="text"
                placeholder="MM/YY"
                readOnly
                css={css`
                  flex: 1;
                  height: 40px;
                  padding: 0 12px;
                  border-radius: 6px;
                  border: 1px solid ${getColor(vars.colors.border)};
                  font-size: 14px;
                  outline: none;
                  font-family: inherit;
                `}
              />
              <input
                type="text"
                placeholder="CVC"
                readOnly
                css={css`
                  flex: 1;
                  height: 40px;
                  padding: 0 12px;
                  border-radius: 6px;
                  border: 1px solid ${getColor(vars.colors.border)};
                  font-size: 14px;
                  outline: none;
                  font-family: inherit;
                `}
              />
            </div>
            <div
              css={css`
                display: flex;
                gap: 12px;
              `}
            >
              <input
                type="text"
                placeholder="United States"
                defaultValue="United States"
                readOnly
                css={css`
                  flex: 1;
                  height: 40px;
                  padding: 0 12px;
                  border-radius: 6px;
                  border: 1px solid ${getColor(vars.colors.border)};
                  font-size: 14px;
                  outline: none;
                  font-family: inherit;
                `}
              />
              <input
                type="text"
                placeholder="12345"
                readOnly
                css={css`
                  flex: 1;
                  height: 40px;
                  padding: 0 12px;
                  border-radius: 6px;
                  border: 1px solid ${getColor(vars.colors.border)};
                  font-size: 14px;
                  outline: none;
                  font-family: inherit;
                `}
              />
            </div>
          </div>
        </div>

        {/* Apple Pay / Google Pay */}
        {(['Apple Pay', 'Google Pay'] as const).map(method => (
          <div
            key={method}
            css={css`
              display: flex;
              align-items: center;
              gap: 12px;
              padding: 0 16px;
              height: 44px;
              border-radius: 8px;
              border: 1px solid ${getColor(vars.colors.border)};
              margin-bottom: 8px;
            `}
          >
            <div
              css={css`
                width: 16px;
                height: 16px;
                border-radius: 50%;
                border: 2px solid #b9b9ac;
              `}
            />
            <svg width="20" height="14" viewBox="0 0 20 14" fill="none">
              <rect x="0.5" y="0.5" width="19" height="13" rx="2" fill="#f5f5f5" stroke="#ddd" />
            </svg>
            <span
              css={css`
                font-size: 14px;
                color: ${getColor(vars.colors.content)};
              `}
            >
              {method}
            </span>
          </div>
        ))}

        {/* Bump offer */}
        {data.bumpProduct && (
          <div
            css={css`
              margin-top: 24px;
              margin-bottom: 32px;
              border-radius: 10px;
              border: 1px solid ${getColor(vars.colors.border)};
              box-shadow:
                0 2px 3px rgba(0, 0, 0, 0.05),
                0 1px 1px rgba(0, 0, 0, 0.05);
              display: flex;
              gap: 24px;
              padding: 24px;
              position: relative;
            `}
          >
            {/* Image */}
            <div
              css={css`
                width: 200px;
                height: 200px;
                border-radius: 4px;
                overflow: hidden;
                flex-shrink: 0;
              `}
            >
              <img
                src={wandPatternImg}
                alt=""
                css={css`
                  width: 100%;
                  height: 100%;
                  object-fit: cover;
                `}
              />
            </div>

            {/* Badge */}
            <div
              css={css`
                position: absolute;
                top: 27px;
                left: 27px;
                padding: 0 12px;
                height: 26px;
                display: flex;
                align-items: center;
                justify-content: center;
                border-radius: 4px;
                font-size: 12px;
                font-weight: 500;
                background: white;
                border: 1px solid ${getColor(vars.colors.border)};
                color: ${getColor(vars.colors.content)};
              `}
            >
              {data.bumpOfferTitle || 'Bonus offer'}
            </div>

            {/* Content */}
            <div
              css={css`
                display: flex;
                flex-direction: column;
                justify-content: space-between;
                flex: 1;
                min-height: 200px;
              `}
            >
              <div>
                <p
                  css={css`
                    font-size: 16px;
                    font-weight: 500;
                    line-height: 24px;
                    color: ${getColor(vars.colors.content)};
                    margin-bottom: 8px;
                  `}
                >
                  {data.bumpProduct}
                </p>
                <div
                  css={css`
                    font-size: 14px;
                    line-height: 20px;
                    color: ${getColor(vars.colors.content)};
                  `}
                >
                  <p>
                    The perfect project to use your leftover felt scraps and create another
                    heartfelt gift for your little one :) Matching patterns for the Birthday Crown
                    themes (2x per...
                  </p>
                  <p
                    css={css`
                      text-decoration: underline;
                      color: rgba(57, 48, 45, 0.52);
                    `}
                  >
                    See more
                  </p>
                </div>
              </div>

              {/* Price row + button */}
              <div
                css={css`
                  display: flex;
                  align-items: center;
                  justify-content: space-between;
                `}
              >
                <span
                  css={css`
                    font-size: 0;
                    line-height: 0;
                  `}
                >
                  <span
                    css={css`
                      font-size: 18px;
                      font-weight: 500;
                      color: #d33003;
                      line-height: 23.2px;
                    `}
                  >
                    $12.00
                  </span>{' '}
                  <span
                    css={css`
                      font-size: 12px;
                      text-decoration: line-through;
                      line-height: 17.8px;
                      color: ${getColor(vars.colors.content)};
                    `}
                  >
                    $19.99
                  </span>
                </span>
                <button
                  type="button"
                  css={css`
                    height: 40px;
                    padding: 0 17px;
                    border-radius: 6px;
                    border: 1px solid ${getColor(vars.colors.border)};
                    background: white;
                    display: flex;
                    align-items: center;
                    justify-content: center;
                    font-size: 14px;
                    font-weight: 500;
                    color: ${getColor(vars.colors.content)};
                    cursor: pointer;
                    font-family: inherit;
                  `}
                >
                  Add to order
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Pay button */}
        <button
          type="button"
          css={css`
            width: 100%;
            height: 48px;
            border-radius: 8px;
            border: none;
            background: ${getColor(vars.colors.content)};
            color: white;
            font-size: 14px;
            font-weight: 500;
            cursor: pointer;
            margin-top: ${data.bumpProduct ? '0' : '32px'};
            margin-bottom: 12px;
            font-family: inherit;
          `}
        >
          Pay {payAmount || '$XX'} now
        </button>

        <p
          css={css`
            font-size: 12px;
            text-align: center;
            margin-bottom: 32px;
            color: ${getColor(vars.colors.content2)};
          `}
        >
          100% secure payments by{' '}
          <strong
            css={css`
              color: ${getColor(vars.colors.content)};
            `}
          >
            Stripe
          </strong>
        </p>

        <hr
          css={css`
            margin-bottom: 32px;
            border: none;
            border-top: 1px solid ${getColor(vars.colors.border)};
          `}
        />

        {/* Testimonial */}
        {(data.testimonialName || data.testimonialText) && (
          <div
            css={css`
              display: flex;
              flex-direction: column;
              align-items: center;
              text-align: center;
              margin-bottom: 32px;
            `}
          >
            {data.testimonialImage && (
              <div
                css={css`
                  width: 80px;
                  height: 80px;
                  border-radius: 50%;
                  overflow: hidden;
                  margin-bottom: 16px;
                `}
              >
                <img
                  src={data.testimonialImage}
                  alt=""
                  css={css`
                    width: 100%;
                    height: 100%;
                    object-fit: cover;
                  `}
                />
              </div>
            )}
            {data.testimonialText && (
              <p
                css={css`
                  font-size: 16px;
                  margin-bottom: 12px;
                  color: ${getColor(vars.colors.content)};
                  font-family: 'Flodesk JJannon', Georgia, serif;
                  font-style: italic;
                `}
              >
                &ldquo;{data.testimonialText}&rdquo;
              </p>
            )}
            {data.testimonialName && (
              <p
                css={css`
                  font-size: 14px;
                  font-weight: 500;
                  color: ${getColor(vars.colors.content)};
                `}
              >
                {data.testimonialName}
              </p>
            )}
          </div>
        )}
      </div>

      {/* Right column — order summary */}
      <div
        css={css`
          width: 260px;
          flex-shrink: 0;
        `}
      >
        <h3
          css={css`
            font-size: 16px;
            font-weight: 500;
            margin-bottom: 16px;
            color: ${getColor(vars.colors.content)};
          `}
        >
          Your order summary
        </h3>

        {data.productImage && (
          <div
            css={css`
              width: 100%;
              aspect-ratio: 1;
              border-radius: 8px;
              overflow: hidden;
              margin-bottom: 16px;
              background: ${getColor(vars.colors.background3)};
            `}
          >
            <img
              src={data.productImage}
              alt=""
              css={css`
                width: 100%;
                height: 100%;
                object-fit: cover;
              `}
            />
          </div>
        )}

        {data.productName && (
          <p
            css={css`
              font-size: 14px;
              font-weight: 500;
              margin-bottom: 4px;
              color: ${getColor(vars.colors.content)};
            `}
          >
            {data.productName}
          </p>
        )}
        {data.productDesc && (
          <p
            css={css`
              font-size: 14px;
              margin-bottom: 12px;
              color: ${getColor(vars.colors.content2)};
            `}
          >
            {data.productDesc}
          </p>
        )}

        {priceStr && (
          <div
            css={css`
              display: flex;
              align-items: baseline;
              justify-content: flex-end;
              gap: 8px;
              margin-bottom: 24px;
            `}
          >
            {hasSale && (
              <span
                css={css`
                  font-size: 16px;
                  font-weight: 500;
                  color: #d33003;
                `}
              >
                {salePriceStr}
              </span>
            )}
            <span
              css={css`
                font-size: ${hasSale ? '14px' : '16px'};
                font-weight: ${hasSale ? '400' : '500'};
                color: ${hasSale ? getColor(vars.colors.content2) : getColor(vars.colors.content)};
                text-decoration: ${hasSale ? 'line-through' : 'none'};
              `}
            >
              {priceStr}
            </span>
          </div>
        )}

        <hr
          css={css`
            margin-bottom: 16px;
            border: none;
            border-top: 1px solid ${getColor(vars.colors.border)};
          `}
        />

        <input
          type="text"
          placeholder="Discount code"
          readOnly
          css={css`
            width: 100%;
            height: 40px;
            padding: 0 12px;
            border-radius: 6px;
            border: 1px solid ${getColor(vars.colors.border)};
            font-size: 14px;
            outline: none;
            margin-bottom: 16px;
            font-family: inherit;
          `}
        />

        <hr
          css={css`
            margin-bottom: 16px;
            border: none;
            border-top: 1px solid ${getColor(vars.colors.border)};
          `}
        />

        <div
          css={css`
            display: flex;
            align-items: center;
            justify-content: space-between;
          `}
        >
          <span
            css={css`
              font-size: 16px;
              font-weight: 500;
              color: ${getColor(vars.colors.content)};
            `}
          >
            Total
          </span>
          <div
            css={css`
              display: flex;
              align-items: baseline;
              gap: 8px;
            `}
          >
            <span
              css={css`
                font-size: 12px;
                color: ${getColor(vars.colors.content2)};
              `}
            >
              USD
            </span>
            <span
              css={css`
                font-size: 16px;
                font-weight: 500;
                color: ${getColor(vars.colors.content)};
              `}
            >
              {hasSale ? salePriceStr : priceStr || '$0'}
            </span>
          </div>
        </div>
      </div>
    </div>
  );
}

// ─── Confirmation view ────────────────────────────────────────────────────────

function ConfirmationView({ data }: { data: PreviewData }) {
  const priceStr = getEffectivePrice(data);
  const salePriceStr = getSalePrice(data);
  const effectivePrice =
    salePriceStr && data.onSaleActive && !data.isRecurring ? salePriceStr : priceStr || '$0';

  return (
    <div
      css={css`
        display: flex;
        flex-direction: column;
        align-items: center;
        padding: 64px 32px;
        background: ${getColor(vars.colors.background2)};
        min-height: 100%;
      `}
    >
      <h1
        css={css`
          font-size: 30px;
          font-weight: 500;
          text-align: center;
          margin-bottom: 40px;
          color: ${getColor(vars.colors.content)};
          line-height: 34px;
        `}
      >
        Thank you for your purchase!
      </h1>

      {/* Next steps card */}
      <div
        css={css`
          width: 500px;
          background: white;
          border-radius: 8px;
          padding: 40px;
          display: flex;
          flex-direction: column;
          align-items: center;
          gap: 24px;
          margin-bottom: 40px;
          box-shadow:
            0px 1.98px 3.06px 0px rgba(0, 0, 0, 0.05),
            0px 1px 1px 0px rgba(0, 0, 0, 0.05);
        `}
      >
        <div
          css={css`
            text-align: center;
          `}
        >
          <h2
            css={css`
              font-size: 24px;
              font-weight: 500;
              margin-bottom: 12px;
              color: ${getColor(vars.colors.content)};
            `}
          >
            {data.confirmTitle || 'Order confirmation'}
          </h2>
          <p
            css={css`
              font-size: 14px;
              color: ${getColor(vars.colors.content)};
            `}
          >
            {data.confirmDesc ||
              'You will soon receive an email with the receipt and instructions.'}
          </p>
        </div>
        <button
          type="button"
          css={css`
            height: 48px;
            padding: 0 20px;
            border-radius: 6px;
            border: none;
            background: ${getColor(vars.colors.content)};
            color: white;
            font-size: 14px;
            font-weight: 500;
            width: 200px;
            cursor: pointer;
            font-family: inherit;
          `}
        >
          {data.confirmBtnText || 'Download now'}
        </button>
      </div>

      {/* Order details card */}
      <div
        css={css`
          width: 500px;
          box-shadow:
            0px 5.881px 14.045px 0px rgba(0, 0, 0, 0.09),
            0px 1.98px 3.06px 0px rgba(0, 0, 0, 0.05),
            0px 1px 1px 0px rgba(0, 0, 0, 0.05);
        `}
      >
        {/* Zigzag top border */}
        <div
          css={css`
            width: 100%;
            height: 8px;
            background: repeating-linear-gradient(
              90deg,
              transparent,
              transparent 4px,
              ${getColor(vars.colors.background2)} 4px,
              ${getColor(vars.colors.background2)} 8px
            );
            background-color: white;
          `}
        />
        <div
          css={css`
            background: white;
            border-radius: 0 0 8px 8px;
            padding: 40px 32px;
          `}
        >
          {/* Product line item */}
          <div
            css={css`
              display: flex;
              gap: 12px;
              align-items: flex-start;
              margin-bottom: 24px;
            `}
          >
            {data.productImage && (
              <div
                css={css`
                  width: 76px;
                  height: 76px;
                  border-radius: 4px;
                  overflow: hidden;
                  flex-shrink: 0;
                  background: ${getColor(vars.colors.background3)};
                `}
              >
                <img
                  src={data.productImage}
                  alt=""
                  css={css`
                    width: 100%;
                    height: 100%;
                    object-fit: cover;
                  `}
                />
              </div>
            )}
            <div
              css={css`
                flex: 1;
                min-width: 0;
              `}
            >
              <div
                css={css`
                  display: flex;
                  align-items: flex-start;
                  justify-content: space-between;
                `}
              >
                <p
                  css={css`
                    font-size: 14px;
                    font-weight: 500;
                    color: ${getColor(vars.colors.content)};
                  `}
                >
                  {data.productName || 'Product'}
                </p>
                <span
                  css={css`
                    font-size: 14px;
                    flex-shrink: 0;
                    margin-left: 16px;
                    color: ${getColor(vars.colors.content)};
                  `}
                >
                  {effectivePrice}
                </span>
              </div>
              {data.productDesc && (
                <p
                  css={css`
                    font-size: 14px;
                    margin-top: 2px;
                    color: ${getColor(vars.colors.content2)};
                  `}
                >
                  {data.productDesc}
                </p>
              )}
            </div>
          </div>

          {/* Bump line item */}
          {data.bumpProduct && (
            <div
              css={css`
                display: flex;
                gap: 12px;
                align-items: flex-start;
                margin-bottom: 24px;
              `}
            >
              {data.productImage && (
                <div
                  css={css`
                    width: 76px;
                    height: 76px;
                    border-radius: 4px;
                    overflow: hidden;
                    flex-shrink: 0;
                    background: ${getColor(vars.colors.background3)};
                  `}
                >
                  <img
                    src={data.productImage}
                    alt=""
                    css={css`
                      width: 100%;
                      height: 100%;
                      object-fit: cover;
                    `}
                  />
                </div>
              )}
              <div
                css={css`
                  flex: 1;
                  min-width: 0;
                `}
              >
                <div
                  css={css`
                    display: flex;
                    align-items: flex-start;
                    justify-content: space-between;
                  `}
                >
                  <p
                    css={css`
                      font-size: 14px;
                      font-weight: 500;
                      color: ${getColor(vars.colors.content)};
                    `}
                  >
                    {data.bumpProduct}
                  </p>
                  <span
                    css={css`
                      font-size: 14px;
                      flex-shrink: 0;
                      margin-left: 16px;
                      color: ${getColor(vars.colors.content)};
                    `}
                  >
                    $12.00
                  </span>
                </div>
              </div>
            </div>
          )}

          <hr
            css={css`
              margin-bottom: 24px;
              border: none;
              border-top: 1px solid ${getColor(vars.colors.border)};
            `}
          />

          <p
            css={css`
              font-size: 14px;
              font-weight: 500;
              margin-bottom: 16px;
              color: ${getColor(vars.colors.content)};
            `}
          >
            Price details
          </p>

          <div
            css={css`
              display: flex;
              align-items: center;
              justify-content: space-between;
              margin-bottom: 8px;
            `}
          >
            <span
              css={css`
                font-size: 14px;
                color: ${getColor(vars.colors.content)};
              `}
            >
              Subtotal
            </span>
            <span
              css={css`
                font-size: 14px;
                color: ${getColor(vars.colors.content)};
              `}
            >
              {effectivePrice}
            </span>
          </div>
          <div
            css={css`
              display: flex;
              align-items: center;
              justify-content: space-between;
              margin-bottom: 24px;
            `}
          >
            <span
              css={css`
                font-size: 14px;
                color: ${getColor(vars.colors.content)};
              `}
            >
              Sales tax
            </span>
            <span
              css={css`
                font-size: 14px;
                color: ${getColor(vars.colors.content)};
              `}
            >
              $0.00
            </span>
          </div>

          <hr
            css={css`
              margin-bottom: 24px;
              border: none;
              border-top: 1px solid ${getColor(vars.colors.border)};
            `}
          />

          <div
            css={css`
              display: flex;
              align-items: center;
              justify-content: space-between;
            `}
          >
            <span
              css={css`
                font-size: 14px;
                font-weight: 500;
                color: ${getColor(vars.colors.content)};
              `}
            >
              Total
            </span>
            <div
              css={css`
                display: flex;
                align-items: baseline;
                gap: 4px;
              `}
            >
              <span
                css={css`
                  font-size: 12px;
                  font-weight: 500;
                  color: ${getColor(vars.colors.content2)};
                `}
              >
                USD
              </span>
              <span
                css={css`
                  font-size: 14px;
                  font-weight: 500;
                  color: ${getColor(vars.colors.content)};
                `}
              >
                {effectivePrice}
              </span>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

// ─── Browser chrome frame ─────────────────────────────────────────────────────

function BrowserFrame({ activeTab, data }: { activeTab: ActiveTab; data: PreviewData }) {
  return (
    <div
      css={css`
        position: absolute;
        left: 50%;
        transform: translateX(-50%);
        top: 99px;
        width: 1080px;
        border-radius: 20px;
        overflow: hidden;
        height: calc(100vh - 99px - 35px);
        box-shadow:
          0px 5.881px 14.045px -1.944px rgba(0, 0, 0, 0.09),
          0px 1.98px 3.06px -0.36px rgba(0, 0, 0, 0.05),
          0px 1px 1px -0.1px rgba(0, 0, 0, 0.05);
      `}
    >
      {/* Title bar */}
      <div
        css={css`
          height: 40px;
          background: white;
          display: flex;
          align-items: center;
          padding: 0 20px;
          border-bottom: 1px solid rgba(0, 0, 0, 0.05);
          flex-shrink: 0;
        `}
      >
        <div
          css={css`
            display: flex;
            gap: 8px;
          `}
        >
          {(['#FF605C', '#FFBD44', '#00CA4E'] as const).map(color => (
            <div
              key={color}
              css={css`
                width: 12px;
                height: 12px;
                border-radius: 50%;
                background: ${color};
              `}
            />
          ))}
        </div>
      </div>

      {/* Scrollable content */}
      <div
        css={css`
          height: calc(100% - 40px);
          overflow-y: auto;
          background: white;
          scrollbar-width: none;
          &::-webkit-scrollbar {
            display: none;
          }
        `}
      >
        {activeTab === 'checkout' ? <CheckoutView data={data} /> : <ConfirmationView data={data} />}
      </div>
    </div>
  );
}

// ─── Page ─────────────────────────────────────────────────────────────────────

export default function PreviewPage() {
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState<ActiveTab>('checkout');
  const [data, setData] = useState<PreviewData>({});

  useEffect(() => {
    const raw = localStorage.getItem('previewData');
    if (raw) {
      try {
        setData(JSON.parse(raw) as PreviewData);
      } catch {
        // ignore invalid JSON
      }
    }
  }, []);

  return (
    <div
      css={css`
        height: 100vh;
        overflow: hidden;
        background: ${getColor(vars.colors.background2)};
        color: ${getColor(vars.colors.content)};
        font-family:
          'Flodesk Sans',
          -apple-system,
          BlinkMacSystemFont,
          'Segoe UI',
          system-ui,
          sans-serif;
        * {
          box-sizing: border-box;
          margin: 0;
          padding: 0;
        }
        input,
        textarea,
        select,
        button {
          font-family: inherit;
        }
      `}
    >
      {/* Top nav */}
      <nav
        css={css`
          position: fixed;
          top: 0;
          left: 0;
          right: 0;
          height: 64px;
          background: white;
          z-index: 50;
          display: flex;
          align-items: center;
          justify-content: space-between;
          padding: 0 20px;
          border-bottom: 1px solid ${getColor(vars.colors.border)};
        `}
      >
        {/* Back button */}
        <Button
          variant="neutral"
          icon={
            <svg width="14" height="14" viewBox="0 0 14 14" fill="none">
              <path
                d="M12 7H2M2 7L7 2M2 7L7 12"
                stroke={getColor(vars.colors.content)}
                strokeWidth="1.5"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
            </svg>
          }
          iconPosition="left"
          onClick={() => navigate('/')}
        >
          Back
        </Button>

        {/* Center tab toggle */}
        <div
          css={css`
            position: absolute;
            left: 50%;
            top: 50%;
            transform: translate(-50%, -50%);
          `}
        >
          <TextToggleGroup>
            <TextToggle
              isActive={activeTab === 'checkout'}
              onClick={() => setActiveTab('checkout')}
            >
              Product checkout
            </TextToggle>
            <TextToggle
              isActive={activeTab === 'confirmation'}
              onClick={() => setActiveTab('confirmation')}
            >
              Confirmation page
            </TextToggle>
          </TextToggleGroup>
        </div>

        {/* Right icons */}
        <div
          css={css`
            display: flex;
            align-items: center;
            gap: 16px;
          `}
        >
          <button
            type="button"
            title="Desktop view"
            aria-label="Desktop view"
            css={css`
              width: 36px;
              height: 36px;
              display: flex;
              align-items: center;
              justify-content: center;
              border-radius: 4px;
              border: none;
              background: none;
              cursor: pointer;
            `}
          >
            <svg width="18" height="18" viewBox="0 0 18 18" fill="none">
              <rect
                x="1"
                y="1.5"
                width="16"
                height="12"
                rx="1.5"
                stroke={getColor(vars.colors.content)}
                strokeWidth="1.2"
              />
              <line
                x1="9"
                y1="13.5"
                x2="9"
                y2="16"
                stroke={getColor(vars.colors.content)}
                strokeWidth="1.2"
              />
              <line
                x1="6"
                y1="16"
                x2="12"
                y2="16"
                stroke={getColor(vars.colors.content)}
                strokeWidth="1.2"
                strokeLinecap="round"
              />
            </svg>
          </button>
          <button
            type="button"
            title="Mobile view"
            aria-label="Mobile view"
            css={css`
              width: 36px;
              height: 36px;
              display: flex;
              align-items: center;
              justify-content: center;
              border-radius: 4px;
              border: none;
              background: none;
              cursor: pointer;
            `}
          >
            <svg width="12" height="18" viewBox="0 0 12 18" fill="none">
              <rect
                x="0.5"
                y="0.5"
                width="11"
                height="17"
                rx="2"
                stroke={getColor(vars.colors.content)}
                strokeWidth="1"
              />
              <line
                x1="4"
                y1="15"
                x2="8"
                y2="15"
                stroke={getColor(vars.colors.content)}
                strokeWidth="1"
                strokeLinecap="round"
              />
            </svg>
          </button>
        </div>
      </nav>

      {/* Browser frame */}
      <BrowserFrame activeTab={activeTab} data={data} />
    </div>
  );
}
